import { useEffect, useMemo } from 'react';

import { EmptyState, Spinner } from '../../components';
import { ChartBarIcon } from '../../layout/icons';
import type { Id } from '../../api/types';
import { useIzteachSessionStore } from '../../stores/izteach';
import { useQltSlotAttendanceStore } from '../../stores/qlt';
import { rosterOf, vnDate } from '../submissions/submissions';
import { examLoadKey } from './classExams';
import { IZ_SIGNED_OUT, useExamResultStore } from './store';
import { examDisplayTitle, groupExams } from './examResults';
import '../../styles/submissions.css';

export interface ExamsPanelProps {
  classTitle: string;
  /** The Kho bài tree query; its courses name the exams to read. */
  classId: Id;
  gradeId?: Id;
  subjectId?: Id;
  onOpen: (examId: string) => void;
}

// Bài kiểm tra: the IZ unit exams this period's class sat; counts only, the card behind a row shows the rest
export function ExamsPanel({ classTitle, classId, gradeId, subjectId, onOpen }: ExamsPanelProps) {
  const rows = useQltSlotAttendanceStore((state) => state.rows);
  const rosterLoading = useQltSlotAttendanceStore((state) => state.loading);
  const loading = useExamResultStore((state) => state.loading);
  const error = useExamResultStore((state) => state.error);
  const attempts = useExamResultStore((state) => state.attempts);
  const titles = useExamResultStore((state) => state.courseTitles);
  const examTitles = useExamResultStore((state) => state.examTitles);
  const load = useExamResultStore((state) => state.load);
  const signedIn = useIzteachSessionStore((state) => Boolean(state.accessToken));

  const roster = useMemo(() => rosterOf(rows), [rows]);
  const codes = useMemo(() => roster.map((student) => student.code), [roster]);
  const input = { codes, classId, gradeId, subjectId };
  const key = examLoadKey(input, signedIn);
  useEffect(() => { if (codes.length > 0) void load(key, input); }, [key]);

  const exams = useMemo(() => groupExams(attempts, roster), [attempts, roster]);

  if (roster.length === 0) {
    if (rosterLoading) return <Spinner label="Đang tải danh sách lớp…" />;
    return <EmptyState icon={<ChartBarIcon size={26} />} title="Chưa có danh sách lớp" description="Bảng chưa tải được danh sách học sinh của tiết này nên chưa tìm được bài kiểm tra." />;
  }
  if (loading && exams.length === 0) return <Spinner label="Đang tìm bài kiểm tra của lớp…" />;
  if (error) return <EmptyState icon={<ChartBarIcon size={26} />} title={error === IZ_SIGNED_OUT ? 'Chưa đăng nhập IZTeach' : 'Chưa tải được kết quả'} description={error} action={<button type="button" className="secondary-button" onClick={() => void load(key, input, true)}>Thử lại</button>} />;
  if (exams.length === 0) return <EmptyState icon={<ChartBarIcon size={26} />} title="Chưa có bài kiểm tra nào" description={`Học sinh lớp ${classTitle || 'này'} chưa làm bài kiểm tra nào trên IZTeach.`} action={<button type="button" className="secondary-button" onClick={() => void load(key, input, true)}>Tải lại</button>} />;

  const years: string[] = [];
  exams.forEach((exam) => { if (years.indexOf(exam.schoolYear) < 0) years.push(exam.schoolYear); });
  return (
    <div className="hw-panel">
      <div className="hw-panel__bar">
        <p>Bài kiểm tra học sinh lớp {classTitle || 'này'} đã làm trên IZTeach.</p>
        <button type="button" className="hw-panel__refresh" disabled={loading} onClick={() => void load(key, input, true)}>{loading ? 'Đang tải…' : 'Tải lại'}</button>
      </div>
      {years.map((year) => (
        <section key={year || 'none'} className="hw-panel__year">
          <h3>{year ? `Năm học ${year}` : 'Chưa rõ ngày làm'}</h3>
          <ul className="up-list">
            {exams.filter((exam) => exam.schoolYear === year).map((exam) => (
              <li key={exam.examId}>
                <button type="button" className="up-list__row" onClick={() => onOpen(exam.examId)}>
                  <span className="up-list__mark" aria-hidden={true}><ChartBarIcon size={18} /></span>
                  <span className="up-list__text">
                    <span className="up-list__name" title={examDisplayTitle(exam, examTitles)}>{examDisplayTitle(exam, examTitles)}</span>
                    <span className="up-list__meta">{[`${exam.submitted}/${roster.length} đã nộp`, exam.doing ? `${exam.doing} đang làm` : '', examTitles[exam.examId] && exam.firstStart ? vnDate(exam.firstStart) : ''].filter(Boolean).join(' • ')}</span>
                    {titles[exam.courseId] ? <span className="up-list__meta" title={titles[exam.courseId]}>{titles[exam.courseId]}</span> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
